<template>
  <AppStats :items="stats" :featured="true" />
</template>

<script lang="ts" setup>
import { computed, ref, Ref, watch } from 'vue'
import AppStats, { StatItem } from '@/components/app/AppStats.vue'
import { useQuery } from '@tanstack/vue-query'
import { orderByDesc, dateRangeConstants, dateRangeFilter } from '@/utils/queryFilters'
import ProposalService from '@/services/ProposalService'
import { PROPOSAL_STATUS } from '@/models/proposal'
import { currency } from '@/utils'

const props = defineProps({
  statsDateRange: { 
    type: String,
    required: true,
    default: dateRangeConstants.this_month
  }
})

const statsDateRange = ref(props.statsDateRange)

watch(() => props.statsDateRange, (newVal) => {
  statsDateRange.value = newVal
})
const { data: proposals } = useQuery({
  queryKey: ['proposal', 'opportunityGraph', statsDateRange],
  queryFn: async () => {
    const data = await new ProposalService().where(
      [dateRangeFilter('contractDate', statsDateRange.value)],
      [orderByDesc('contractDate')]
    )

    return data
  }
})

const acceptedProposal = computed(() => {
  return proposals.value ?
    proposals.value
      .filter(proposal => proposal.status === PROPOSAL_STATUS.ACCEPTED)
      .map(proposal => [new Date(proposal.contractDate).getTime(), (proposal.contractTotal || 0)])
    : []
})
const awaitingProposal = computed(() => {
  return proposals.value ?
    proposals.value
      .filter(proposal => proposal.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE)
      .map(proposal => [new Date(proposal.contractDate).getTime(), (proposal.contractTotal || 0)])
    : []
})
const SentProposal = computed(() => {
  return proposals.value ?
    proposals.value.map(proposal => [new Date(proposal.contractDate).getTime(), (proposal.contractTotal || 0)])
    : []
})

const bidsCount = computed(() => proposals.value?.length || '--')
const totalAccepted = computed(() => {
  if (!acceptedProposal.value) return '--'
  const total = acceptedProposal.value.reduce((acc, [, total]) => acc + total, 0)
  return total == 0 ? '--' : currency(total)
})
const totalAwaiting = computed(() => {
  if (!awaitingProposal.value) return '--'
  const total = awaitingProposal.value.reduce((acc, [, total]) => acc + total, 0)
  return total == 0 ? '--' : currency(total)
})
const totalSent = computed(() => {
  if (!SentProposal.value) return '--'
  const total = SentProposal.value.reduce((acc, [, total]) => acc + total, 0)
  return total == 0 ? '--' : currency(total)
})

const stats: Ref<StatItem[]> = ref([
  { label: 'bids', name: 'Bids', value: bidsCount },
  { label: 'opportunities', name: 'Opportunities', value: totalSent },
  { label: 'accepted', name: 'Accepted', value: totalAccepted },
  { label: 'awaiting', name: 'Awaiting', value: totalAwaiting },
])
</script>